import { createAsyncThunk } from "@reduxjs/toolkit";
import { InitState } from "@/types";
import { sanitizeSource } from "./initHelper";
import { isValidArray } from "@/lib/utils";
import { httpGet, httpPost } from "@/lib/http";
import { serializedError } from "../helper";

export const initApp = createAsyncThunk(
  "init/initialization",
  async (forceRefresh: boolean, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as { init: InitState };
      const _source = state.init.source;
      const _topics = state.init.topics;

      if (isValidArray(_source) && isValidArray(_topics) && !forceRefresh) {
        return {
          source: _source,
          topics: _topics
        }
      }

      let res = await httpGet({ url: 'source', dispatch });
      if (!res) {
        return rejectWithValue("Failed to fetch source")
      }

      // filter out deleted sources and map to flat structure
      const source = (res.data || []).filter((sour: any) => !sour.deleted).map((sour: any) => ({
        ...sour,
        subSubject: sour.subSubject?.filter((a: any) => !a.deleted)?.map((subSour: any) => ({
          ...subSour,
          publishers: subSour.publishers?.filter((a: any) => !a.deleted)?.map((pub: any) => ({
            ...pub,
            books: pub.books?.filter((a: any) => !a.deleted)?.map((book: any) => ({
              ...book,
              chapters: book.chapters?.filter((a: any) => !a.deleted),
            })),
          })),
        })),
        sortOrder: parseInt(sour.sortOrder) || 0,
      }));
      source.sort((a: any, b: any) => a.sortOrder - b.sortOrder)

      const allSubjects = source.map((sour: any) => ({ id: sour.id, name: sour.subjectName || '' }));

      const allSubSubjects = source.flatMap((sour: any) =>
        sour.subSubject?.map((subSour: any) => ({ id: subSour.id, name: subSour.subSubjectName || '' })) || []
      );

      const allPublishers = source.flatMap((sour: any) =>
        sour.subSubject?.flatMap((subSour: any) =>
          subSour.publishers?.map((pub: any) => ({ id: pub.id, name: pub.publisherName || '' })) || []
        ) || []
      );

      const allBooks = source.flatMap((sour: any) =>
        sour.subSubject?.flatMap((subSour: any) =>
          subSour.publishers?.flatMap((pub: any) =>
            pub.books?.map((book: any) => ({ id: book.id, name: book.bookName || '' })) || []
          ) || []
        ) || []
      );

      const allChapters = source?.flatMap((sour: any) =>
        sour.subSubject?.flatMap((subSour: any) =>
          subSour.publishers?.flatMap((pub: any) =>
            pub.books?.flatMap((book: any) =>
              book.chapters?.map((chap: any) => ({ id: chap.chapterId, name: chap.chapterName || '' })) || []
            ) || []
          ) || []
        ) || []
      );

      const subjectMap: Record<string, {
        id: string;
        interestIDs: string[];
        subSubjects: Record<string, {
          id: string;
          publishers: Record<string, {
            id: string;
            books: Record<string, {
              id: string;
              chapters: Record<string, string>;
            }>;
          }>;
        }>;
      }> = {};

      // Build lookup map
      source?.forEach((subject: any) => {
        const subjectKey = subject.subjectName?.trim()?.toLowerCase();
        subjectMap[subjectKey] = {
          id: subject.id,
          interestIDs: subject.interestIDs,
          subSubjects: {}
        };

        subject.subSubject?.forEach((sub: any) => {
          const subKey = sub.subSubjectName?.trim()?.toLowerCase();
          subjectMap[subjectKey].subSubjects[subKey] = {
            id: sub.id,
            publishers: {}
          };

          sub.publishers?.forEach((pub: any) => {
            const pubKey = pub.publisherName?.trim()?.toLowerCase();
            subjectMap[subjectKey].subSubjects[subKey].publishers[pubKey] = {
              id: pub.id,
              books: {}
            };

            pub.books?.forEach((book: any) => {
              const bookKey = book.bookName?.trim()?.toLowerCase();
              subjectMap[subjectKey].subSubjects[subKey].publishers[pubKey].books[bookKey] = {
                id: book.id,
                chapters: {}
              };

              book.chapters?.forEach((chapter: any) => {
                const chapterKey = chapter.chapterName?.trim()?.toLowerCase();
                subjectMap[subjectKey].subSubjects[subKey].publishers[pubKey].books[bookKey].chapters[chapterKey] = chapter.chapterId;
              });
            });
          });
        });
      });

      res = await httpGet({ url: 'interests', dispatch });
      if (!res) {
        return rejectWithValue("Failed to fetch interests")
      }

      const topics = res.data;

      return {
        source,
        topics,
        allSubjects,
        allSubSubjects,
        allPublishers,
        allBooks,
        allChapters,
        subjectMap
      };
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const updateSources = createAsyncThunk(
  "init/updateSources",
  async (incoming: any, { rejectWithValue, dispatch }) => {
    try {
      const sanitized = incoming.map(sanitizeSource);

      // httpPost returns null rather than throwing, so leaving this unchecked
      // let a rejected save fall straight through to the caller's success
      // toast. Reject instead, so the caller can tell the two apart.
      const saved = await httpPost({ url: 'admin/sources', payload: sanitized, dispatch });
      if (!saved) {
        return rejectWithValue({ message: "Subjects were not saved.", status: 0 });
      }

      const res = await httpGet({ url: 'source', dispatch });

      return res?.data;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const updateInterest = createAsyncThunk(
  "init/updateInterest",
  async (sources: any, { rejectWithValue, getState, dispatch }) => {
    try {
      // check for new source and remove id
      const state = getState() as { init: InitState };
      const prevIds = (state.init.topics || []).map(({ id }) => id);
      const updated = sources.map((ss: any) => {
        if (!prevIds.includes(ss.id)) {
          ss.id = '';
        }
        return ss;
      });

      const saved = await httpPost({ url: 'admin/interests', payload: updated, dispatch });
      if (!saved) {
        return rejectWithValue({ message: "Interests were not saved.", status: 0 });
      }

      let res = await httpGet({ url: 'interests', dispatch });
      return res?.data;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);
